import * as S from "@effect/schema/Schema";
import { maybeJson } from "./Sqlite.js";
/** Branded Id Schema. To create Id Schema for a specific table, use {@link id}. */
export const Id = S.String.pipe(S.pattern(/^[\w-]{21}$/), S.brand("Id"));
/**
 * A factory function to create {@link Id} Schema for a specific table.
 *
 * @example
 *   import * as S from "@effect/schema/Schema";
 *   import { id } from "@evolu/react";
 *
 *   const TodoId = id("Todo");
 *   type TodoId = typeof TodoId.Type;
 */
export const id = (table) => Id.pipe(S.brand(table));
/**
 * SQLite doesn't support the Date type, so Evolu uses SqliteDate instead. Use
 * the {@link cast} helper to cast SqliteDate from Date and back.
 * https://www.sqlite.org/quirks.html#no_separate_datetime_datatype
 */
export const SqliteDate = S.String.pipe(S.filter((s) => !isNaN(Date.parse(s))), S.brand("SqliteDate"));
/**
 * SQLite doesn't support the boolean type, so Evolu uses SqliteBoolean instead.
 * Use the {@link cast} helper to cast SqliteBoolean from boolean and back.
 * https://www.sqlite.org/quirks.html#no_separate_boolean_datatype
 */
export const SqliteBoolean = S.Number.pipe(S.int(), S.filter((s) => s === 0 || s === 1), S.brand("SqliteBoolean"));
export function cast(value) {
    if (typeof value === "boolean")
        return (value === true ? 1 : 0);
    if (typeof value === "number")
        return value === 1;
    if (value instanceof Date)
        return value.toISOString();
    return new Date(value);
}
/**
 * Create table schema.
 *
 * Supported types are null, string, number, Uint8Array, JSON Object, and JSON
 * Array. Use SqliteDate for dates and SqliteBoolean for booleans.
 *
 * Reserved columns are createdAt, updatedAt, isDeleted. Those columns are added
 * by default.
 *
 * @example
 *   const TodoId = id("Todo");
 *   type TodoId = typeof TodoId.Type;
 *
 *   const TodoTable = table({
 *     id: TodoId,
 *     title: NonEmptyString1000,
 *     isCompleted: S.nullable(SqliteBoolean),
 *   });
 *   type TodoTable = typeof TodoTable.Type;
 */
export const table = (fields) => S.Struct(fields).pipe(S.extend(ReservedColumns));
const ReservedColumns = S.Struct({
    createdAt: SqliteDate,
    updatedAt: SqliteDate,
    isDeleted: SqliteBoolean,
});
/**
 * Create database schema.
 *
 * Tables with a name prefixed with _ are local-only, which means they are not
 * synced. Local-only tables are useful for device-specific or temporal data.
 *
 * @example
 *   const Database = database({
 *     // A local-only table.
 *     _todo: TodoTable,
 *     todo: TodoTable,
 *     todoCategory: TodoCategoryTable,
 *   });
 *   type Database = typeof Database.Type;
 */
export const database = S.Struct;
/**
 * String schema represents a string that is not stringified JSON. Using String
 * schema for strings stored in SQLite is crucial to ensure a stored string is
 * not automatically parsed to a JSON object or array when retrieved. Use String
 * schema for all string-based schemas.
 */
export const String = S.String.pipe(S.filter((s) => {
    if (!maybeJson(s))
        return true;
    try {
        JSON.parse(s);
    }
    catch (e) {
        return true;
    }
    return false;
}, { message: () => "a string that is not stringified JSON" }), S.brand("String"));
/**
 * A string with a maximum length of 1000 characters.
 *
 * @example
 *   import * as S from "@effect/schema/Schema";
 *   import { String1000 } from "@evolu/react";
 *
 *   S.decode(String1000)(value);
 */
export const String1000 = String.pipe(S.maxLength(1000), S.brand("String1000"));
/**
 * A nonempty string with a maximum length of 1000 characters.
 *
 * @example
 *   import * as S from "@effect/schema/Schema";
 *   import { NonEmptyString1000 } from "@evolu/react";
 *
 *   S.decode(NonEmptyString1000)(value);
 */
export const NonEmptyString1000 = String.pipe(S.minLength(1), S.maxLength(1000), S.brand("NonEmptyString1000"));
/**
 * A positive integer.
 *
 * @example
 *   import * as S from "@effect/schema/Schema";
 *   import { PositiveInt } from "@evolu/react";
 *
 *   S.decode(PositiveInt)(value);
 */
export const PositiveInt = S.Number.pipe(S.int(), S.positive(), S.brand("PositiveInt"));
